import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { createAgent, updateAgent, getAgent, getAgentTools, CreateAgentData, WhatsAppConfig, TelegramConfig, ApiConfig, AvailableTool, getAvailableTools, getAgentKnowledgeBase, KnowledgeBaseFile as DatabaseKnowledgeBaseFile } from '../../lib/database';
import { languages } from '../../data/languages';
import { 
  CheckCircleIcon,
  CloseIcon,
  SaveIcon,
  ErrorIcon
} from '../../components/ui/simple-icons';
import Step0TemplateSelection from './components/Step0TemplateSelection';
import Step1BasicInfo from './components/Step1BasicInfo';
import Step2Instructions from './components/Step2Instructions';
import Step3AdvancedSettings from './components/Step3AdvancedSettings';
import { analytics } from '../../lib/analytics';
import Step4DeployAndShare from './components/Step4DeployAndShare';

interface ToolInstance {
  id: string;
  toolId: string;
  name: string;
  description: string;
  tool_prompt: string;
  config: Record<string, any> | null;
  nango_connection_id?: string | null;
}

interface FileContext {
  id: string;
  name: string;
  type: string;
  size: number;
  content: string;
}


interface FormData {
  template: string;
  name: string;
  description: string;
  instructions: string;
  model: string;
  color: string;
  language: string[];
  temperature: number;
  maxTokens: number;
  thinkingBudget: number;
  tools: ToolInstance[];
  fileContext: FileContext[];
  knowledgeBase: DatabaseKnowledgeBaseFile[];
  whatsappConfig: WhatsAppConfig;
  telegramConfig: TelegramConfig;
  apiConfig: ApiConfig;
  sharedUsers: string[];
  sharable?: boolean;
}

const AgentCreationWizard: React.FC = () => {
  const navigate = useNavigate();
  const { agentId } = useParams<{ agentId?: string }>();
  const location = useLocation();
  const isEditMode = Boolean(agentId);
  const [currentStep, setCurrentStep] = useState(isEditMode ? 1 : 0);
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [visitedSteps, setVisitedSteps] = useState<Set<number>>(new Set([isEditMode ? 1 : 0]));
const [initialFormData, setInitialFormData] = useState<FormData | null>(null);
  const [formData, setFormData] = useState<FormData>({
    template: 'blank',
    name: '',
    description: '',
    instructions: '',
    model: 'gemini-2.5-flash',
    color: 'bg-netlify-teal',
    language: [],
    temperature: 0.7,
    maxTokens: 4000,
    thinkingBudget: -1,
    tools: [],
    fileContext: [],
    knowledgeBase: [],
    whatsappConfig: {
      enabled: false,
      twilioAccountSid: '',
      twilioAuthToken: '',
      phoneNumber: '',
      whitelistedNumbers: [],
      unauthorizedMessage: 'Sorry, you are not authorized to use this bot.'
    },
    telegramConfig: {
      enabled: false,
      botToken: '',
      whitelistedUsers: [],
      unauthorizedMessage: 'Sorry, you are not authorized to use this bot.'
    },
    apiConfig: {
      enabled: false,
      apiKey: '',
      apiSecret: '',
    },
    sharedUsers: [],
    sharable: false,
  });

  const totalSteps = 5;

  const stepTitles = [
    'Basic Information & Model',
    'Instructions & Behavior',
    'Advanced Settings',
    'Deploy & Share',
    isEditMode ? 'Review & Update' : 'Review & Create'
  ];

  const stepDescriptions = [
    'Give your agent a name, description, and choose the AI model',
    'Define how your agent should behave and respond',
    'Configure tools and advanced settings',
    'Deploy to platforms and share with others',
    isEditMode ? 'Review your settings and update the agent' : 'Review your settings and create the agent'
  ];

  const models = [
    // Google Models
    { id: 'gemini-2.5-pro', name: 'Gemini 2.5 Pro', provider: 'google', description: 'Enhanced thinking and reasoning, multimodal understanding, advanced coding, and more', cost: '$3.50/1M tokens', context: '2M context', maxTokens: 65536, pages: '~130 pages', supports: 'Audio, images, videos, text, and PDF' },
    { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash', provider: 'google', description: 'Adaptive thinking, cost efficiency', cost: '$0.075/1M tokens', context: '1M context', maxTokens: 32768, pages: '~65 pages', supports: 'Audio, images, videos, and text' },
    { id: 'gemini-2.5-flash-lite', name: 'Gemini 2.5 Flash-Lite', provider: 'google', description: 'Most cost-efficient model supporting high throughput', cost: '$0.0375/1M tokens', context: '1M context', maxTokens: 16384, pages: '~32 pages', supports: 'Text, image, video, audio' }
  ];

  // Load existing agent data in edit mode
  useEffect(() => {
    const loadAgentData = async () => {
      if (!isEditMode || !agentId) return;
      
      try {
        setIsLoading(true);
        const agent = await getAgent(agentId);
        const tools = await getAgentTools(agentId);
        const knowledgeBase = await getAgentKnowledgeBase(agentId);
        
        if (!agent) {
          navigate('/create');
          return;
        }
        
        // Convert tools to ToolInstance format
        const toolInstances: ToolInstance[] = tools.map(tool => ({
          id: tool.id,
          toolId: tool.tool_id,
          name: tool.available_tools?.name || tool.name,
          description: tool.available_tools?.description || tool.description || '',
          tool_prompt: tool.tool_prompt || '',
          config: tool.config,
          nango_connection_id: tool.nango_connection_id
        }));
        
        const loadedFormData = {
          template: 'blank',
          name: agent.name,
          description: agent.description || '',
          instructions: agent.instruction,
          model: agent.model,
          color: agent.color,
          language: agent.language && Array.isArray(agent.language)
            ? agent.language.map(langName => languages.find(l => l.name === langName)?.code).filter((c): c is string => !!c)
            : [],
          temperature: agent.temperature || 0.7,
          maxTokens: agent.max_tokens || 4000,
          thinkingBudget: agent.thinking_budget || 0,
          tools: toolInstances,
          fileContext: [],
          knowledgeBase: knowledgeBase,
          whatsappConfig: agent.whatsappConfig || formData.whatsappConfig,
          telegramConfig: agent.telegramConfig || formData.telegramConfig,
          apiConfig: agent.apiConfig || formData.apiConfig,
          sharedUsers: [],
          sharable: agent.sharable,
        };
        setFormData(loadedFormData);
        setInitialFormData(loadedFormData);
      } catch (error) {
        console.error('Error loading agent:', error);
        showToast('Could not load agent data. Please try again later.', 'error');
        navigate('/create');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadAgentData();
  }, [isEditMode, agentId, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab) {
        const step = parseInt(tab, 10);
        if (!isNaN(step) && step >= 1 && step <= totalSteps) {
            setCurrentStep(step);
            setVisitedSteps(prev => new Set([...Array.from(prev), step]));
        }
    }
  }, [location.search, totalSteps]);

  useEffect(() => {
    const fetchAvailableTools = async () => {
      try {
        const tools = await getAvailableTools();
        console.log('[AgentCreationPage] Fetched Available Tools:', JSON.stringify(tools, null, 2));
        setAvailableTools(tools);
      } catch (error) {
        console.error('Error fetching available tools:', error);
        // Optionally, show a toast notification
      }
    };

    fetchAvailableTools();
  }, []);

  const updateFormData = (key: string, value: any) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const nextStep = async () => {
    // If this is a new agent and we are on step 1, create the agent first
    if (!isEditMode && currentStep === 1) {
      if (!formData.name.trim()) {
        showToast('Please enter an agent name before proceeding.', 'error');
        return;
      }
      
      setIsSubmitting(true);
      try {
        const agentData: CreateAgentData = {
          name: formData.name,
          description: formData.description,
          instruction: formData.instructions,
          model: formData.model,
          color: formData.color,
          language: formData.language.map(code => languages.find(l => l.code === code)?.name).filter(Boolean) as string[],
          temperature: formData.temperature,
          max_tokens: formData.maxTokens,
          thinking_budget: formData.thinkingBudget,
          whatsappConfig: formData.whatsappConfig,
          telegramConfig: formData.telegramConfig,
          apiConfig: formData.apiConfig,
          sharable: formData.sharable,
          knowledgeBase: [],
          tools: []
        };
        
        const newAgent = await createAgent(agentData);
        
        analytics.trackAgentCreated({
          agent_id: newAgent.id,
          model: agentData.model,
          tool_count: 0,
          knowledge_base_file_count: 0,
        });

        // Navigate to the edit page for the new agent, continuing to the next step
        navigate(`/agent/edit/${newAgent.id}?tab=2`, { replace: true });
        
      } catch (error) {
        console.error('Error creating agent after step 1:', error);
        showToast('Failed to create the agent. Please try again.', 'error');
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (currentStep < totalSteps) {
      const newStep = currentStep + 1;
      setCurrentStep(newStep);
      setVisitedSteps(prev => new Set([...Array.from(prev), newStep]));
    }
  };

  const previousStep = () => {
    const minStep = isEditMode ? 1 : 0;
    if (currentStep > minStep) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (step: number) => {
    const minStep = isEditMode ? 1 : 0;
    if (step >= minStep && step <= totalSteps && (isEditMode || visitedSteps.has(step))) {
      setCurrentStep(step);
    }
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [availableTools, setAvailableTools] = useState<AvailableTool[]>([]);
  
  
  // Prompt helper state
  const [showPromptHelper, setShowPromptHelper] = useState(false);
  const [promptHelperMessages, setPromptHelperMessages] = useState<Array<{id: string, type: 'user' | 'assistant', content: string}>>([]);
  const [promptHelperInput, setPromptHelperInput] = useState('');
  const [isPromptHelperLoading, setIsPromptHelperLoading] = useState(false);
  
  // Toast notification state
  const [toast, setToast] = useState<{message: string, type: 'success' | 'error'} | null>(null);
  
  // Language selector state
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [showThinkingDropdown, setShowThinkingDropdown] = useState(false);
  
  // Toast helper function
  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000); // Auto hide after 4 seconds
  };
  
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showLanguageDropdown) {
        const target = event.target as HTMLElement;
        if (!target.closest('.language-dropdown')) {
          setShowLanguageDropdown(false);
        }
      }
      if (showThinkingDropdown) {
        const target = event.target as HTMLElement;
        if (!target.closest('.thinking-dropdown')) {
          setShowThinkingDropdown(false);
        }
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLanguageDropdown, showThinkingDropdown]);

  // Prompt helper functions
  const handlePromptHelperSubmit = async (message: string) => {
    if (!message.trim() || isPromptHelperLoading) return;
    
    const userMessage = {
      id: Date.now().toString(),
      type: 'user' as const,
      content: message
    };
    
    setPromptHelperMessages(prev => [...prev, userMessage]);
    setPromptHelperInput('');
    setIsPromptHelperLoading(true);
    
    try {
      // Simulate AI response - in real implementation, this would call an AI API
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const assistantMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant' as const,
        content: generatePromptHelperResponse(message, formData.instructions)
      };
      
      setPromptHelperMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error getting prompt helper response:', error);
    } finally {
      setIsPromptHelperLoading(false);
    }
  };

  const generatePromptHelperResponse = (userInput: string, currentPrompt: string): string => {
    // Simple response generation - in real app this would be AI-powered
    if (userInput.toLowerCase().includes('help') && !currentPrompt.trim()) {
      return "I'd be happy to help you create a great prompt! Tell me: What should your agent do? For example: 'help customers with support questions', 'write social media content', or 'analyze data'";
    }
    
    if (userInput.toLowerCase().includes('customer') || userInput.toLowerCase().includes('support')) {
      const suggestedPrompt = `You are a helpful customer support agent. Your role is to:
- Assist customers with their questions and concerns
- Provide clear, friendly, and professional responses
- Escalate complex issues when appropriate
- Always maintain a helpful and empathetic tone

When responding to customers:
1. Acknowledge their concern
2. Provide helpful information or solutions
3. Ask follow-up questions if needed
4. Offer additional assistance

Keep responses concise but thorough.`;
      
      updateFormData('instructions', suggestedPrompt);
      return "I've created a customer support prompt for you! You can see it updated in the instructions field. Would you like me to modify anything specific about the tone, responsibilities, or approach?";
    }
    
    if (userInput.toLowerCase().includes('social media') || userInput.toLowerCase().includes('content')) {
      const suggestedPrompt = `You are a creative social media content creator. Your role is to:
- Generate engaging posts for various social media platforms
- Suggest relevant hashtags and captions
- Adapt content for different audiences and platforms
- Stay current with trends and best practices

When creating content:
1. Consider the target audience and platform
2. Use engaging, authentic language
3. Include relevant calls-to-action
4. Suggest optimal posting times and formats

Focus on creativity while maintaining brand consistency.`;
      
      updateFormData('instructions', suggestedPrompt);
      return "I've created a social media content creator prompt! The instructions have been updated. Would you like me to adjust the focus for specific platforms or add any particular requirements?";
    }
    
    return "Could you tell me more about what your agent should do? For example: What tasks should it handle? What tone should it use? Who is the target audience?";
  };

  const handleSave = async () => {
    if (isSaving || !isEditMode || !agentId) {
      console.log('Save cancelled:', { isSaving, isEditMode, agentId });
      return;
    }
    
    try {
      setIsSaving(true);
      
      console.log('Preparing agent data for save...');
      const agentData: Partial<CreateAgentData> = {
        name: formData.name,
        description: formData.description,
        instruction: formData.instructions,
        model: formData.model,
        color: formData.color,
        language: formData.language.map(code => languages.find(l => l.code === code)?.name).filter(Boolean) as string[],
        temperature: formData.temperature,
        max_tokens: formData.maxTokens,
        thinking_budget: formData.thinkingBudget,
        sharable: formData.sharable,
        whatsappConfig: formData.whatsappConfig,
        telegramConfig: formData.telegramConfig,
        apiConfig: formData.apiConfig,
        knowledgeBase: formData.knowledgeBase.map(({ fileName, content }) => ({ fileName, content })),
        tools: formData.tools.map(({ toolId, tool_prompt, config, nango_connection_id }) => ({
          tool_id: toolId,
          tool_prompt: tool_prompt,
          type: 'function', // Assuming all are functions for now
          config: config,
          nango_connection_id: nango_connection_id
        }))
        // Note: whatsappConfig, telegramConfig, and tools are not part of the agents table
        // They would need separate handling if they exist in other tables
      };

      console.log('Saving agent data:', agentData);
      const result = await updateAgent(agentId, agentData);
      console.log('Save result:', result);

      // Track agent update
      const updatedFields = Object.keys(formData).filter(key => {
        const initialValue = initialFormData ? initialFormData[key as keyof FormData] : null;
        const currentValue = formData[key as keyof FormData];
        return JSON.stringify(initialValue) !== JSON.stringify(currentValue);
      });

      analytics.trackAgentUpdated({
        agent_id: result.id,
        updated_fields: updatedFields,
      });
      
      // Show success feedback without navigation
      showToast('Agent updated successfully!', 'success');
    } catch (error) {
      console.error('Error saving agent:', error);
      showToast('Failed to save the agent. Please review your settings and try again.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    
    try {
      setIsSubmitting(true);
      
      const agentData: CreateAgentData = {
        name: formData.name,
        description: formData.description,
        instruction: formData.instructions,
        model: formData.model,
        color: formData.color,
        language: formData.language.map(code => languages.find(l => l.code === code)?.name).filter(Boolean) as string[],
        temperature: formData.temperature,
        max_tokens: formData.maxTokens,
        thinking_budget: formData.thinkingBudget,
        whatsappConfig: formData.whatsappConfig,
        telegramConfig: formData.telegramConfig,
        apiConfig: formData.apiConfig,
        sharable: formData.sharable,
        knowledgeBase: formData.knowledgeBase.map(({ fileName, content }) => ({ fileName, content })),
        tools: formData.tools.map(({ toolId, tool_prompt, config, nango_connection_id }) => ({
          tool_id: toolId,
          tool_prompt: tool_prompt,
          type: 'function',
          config: config,
          nango_connection_id: nango_connection_id
        }))
      };

      let resultAgent;
      if (isEditMode && agentId) {
        console.log('Updating agent with data:', agentData);
        resultAgent = await updateAgent(agentId, agentData);
        console.log('Agent updated successfully:', resultAgent);

        // Track agent update
        const updatedFields = Object.keys(formData).filter(key => {
          const initialValue = initialFormData ? initialFormData[key as keyof FormData] : null;
          const currentValue = formData[key as keyof FormData];
          return JSON.stringify(initialValue) !== JSON.stringify(currentValue);
        });

        analytics.trackAgentUpdated({
          agent_id: resultAgent.id,
          updated_fields: updatedFields,
        });

      } else {
        console.log('Creating agent with data:', agentData);
        resultAgent = await createAgent(agentData);
        console.log('Agent created successfully:', resultAgent);

        // Track agent creation
        analytics.trackAgentCreated({
          agent_id: resultAgent.id,
          model: agentData.model,
          tool_count: agentData.tools?.length || 0,
          knowledge_base_file_count: agentData.knowledgeBase?.length || 0,
        });
      }
      
      // Navigate to the agent
      navigate(`/agent/${resultAgent.id}`, { 
        state: { justCreated: !isEditMode, justUpdated: isEditMode } 
      });
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} agent:`, error);
      showToast(`Failed to ${isEditMode ? 'update' : 'create'} agent. Please review the details and try again.`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  if (isLoading) {
    return (
      <Layout title={isEditMode ? "Edit Agent" : "Create New Agent"} showBackButton onBack={handleBack}>
        <div className="flex-1 flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-netlify-teal border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={isEditMode ? "Edit Agent" : "Create New Agent"} showBackButton onBack={handleBack}>
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Main Wizard Area */}
        <div className="flex-1 flex flex-col bg-white overflow-hidden">
          {/* Progress Bar */}
          <div className="px-4 sm:px-8 py-6 border-b border-gray-200 bg-white flex-shrink-0">
            <div className="max-w-4xl mx-auto">
              {/* Progress Steps - Only show visible steps (1-6) */}
              {currentStep > 0 && (
              <div className="flex items-center justify-between">
                {Array.from({ length: totalSteps }, (_, i) => {
                  const stepNumber = i + 1;
                  const displayNumber = i + 1;
                  const stepIndex = i;
                  const isVisited = visitedSteps.has(stepNumber);
                  const isClickable = isEditMode || isVisited;
                  
                  return (
                    <React.Fragment key={stepNumber}>
                      <div className="flex flex-col items-center space-y-2 min-w-0">
                        <div 
                          className={`w-12 h-12 rounded-full flex items-center justify-center text-sm font-semibold transition-all border-2 ${
                            stepNumber === currentStep 
                              ? 'bg-netlify-teal text-white border-netlify-teal'
                              : stepNumber < currentStep 
                              ? 'bg-green-500 text-white border-green-500'
                              : 'bg-white text-gray-600 border-gray-300'
                          } ${isClickable ? 'cursor-pointer hover:scale-105 hover:shadow-lg' : ''}`}
                          onClick={() => isClickable && goToStep(stepNumber)}
                          title={isClickable ? 'Click to go to this step' : ''}
                        >
                          {stepNumber < currentStep ? (
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                            </svg>
                          ) : (
                            displayNumber
                          )}
                        </div>
                        <span 
                          className={`text-xs text-center transition-all max-w-20 hidden sm:block ${
                            stepNumber === currentStep 
                              ? 'font-medium text-netlify-teal'
                              : stepNumber < currentStep 
                              ? 'font-medium text-green-600'
                              : 'text-gray-500'
                          } ${isClickable ? 'cursor-pointer hover:text-netlify-teal' : ''}`}
                          onClick={() => isClickable && goToStep(stepNumber)}
                          title={isClickable ? 'Click to go to this step' : ''}
                        >
                          {stepTitles[stepIndex]}
                        </span>
                      </div>
                      {i < totalSteps - 1 && (
                        <div className={`flex-1 h-0.5 mx-3 ${
                          stepNumber < currentStep ? 'bg-green-500' : 'bg-gray-300'
                        }`}></div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Form Steps */}
        <div className="flex-1 overflow-y-auto bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-8 py-12">
            
            {/* Step 0: Template Selection (New Agents Only) */}
            {currentStep === 0 && !isEditMode && (
              <Step0TemplateSelection
                formData={formData}
                updateFormData={updateFormData}
              />
            )}
            
            {/* Step 1: Basic Information & Model Selection */}
            {currentStep === 1 && (
              <Step1BasicInfo
                formData={formData}
                updateFormData={updateFormData}
              />
            )}

            {/* Step 2: Instructions */}
            {currentStep === 2 && (
              <Step2Instructions
                formData={formData}
                updateFormData={updateFormData}
                availableTools={availableTools}
                setShowPromptHelper={setShowPromptHelper}
                agentId={agentId}
              />
            )}

            {/* Step 3: Advanced Settings */}
            {currentStep === 3 && (
              <Step3AdvancedSettings
                formData={formData}
                updateFormData={updateFormData}
                models={models}
              />
            )}

            {/* Step 4: Deploy & Share */}
            {currentStep === 4 && (
              <Step4DeployAndShare
                agentId={agentId}
                formData={formData}
                updateFormData={updateFormData}
              />
            )}

                
              </div>
            
            {/* Step 5: Review */}
            {currentStep === 5 && (
              <div className="space-y-8">
                {/* Agent Review Section */}
                <div className="bg-white rounded-xl p-4 sm:p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-6">Review Your Agent</h3>
                  
                  <div className="space-y-6">
                    <div className="flex items-start space-x-4">
                      <div className={`w-12 h-12 rounded-lg ${formData.color} flex-shrink-0`}></div>
                      <div>
                        <h4 className="text-xl font-semibold text-gray-900">{formData.name || 'Unnamed Agent'}</h4>
                        <p className="text-gray-600 mt-1">{formData.description || 'No description provided'}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm">
                      <div>
                        <h5 className="font-medium text-gray-900 mb-2">Template</h5>
                        <p className="text-gray-600 capitalize">{formData.template.replace('_', ' ')}</p>
                      </div>
                      <div>
                        <h5 className="font-medium text-gray-900 mb-2">Model</h5>
                        <p className="text-gray-600">{models.find(m => m.id === formData.model)?.name}</p>
                      </div>
                      <div>
                        <h5 className="font-medium text-gray-900 mb-2">Temperature</h5>
                        <p className="text-gray-600">{formData.temperature}</p>
                      </div>
                      <div>
                        <h5 className="font-medium text-gray-900 mb-2">Max Tokens</h5>
                        <p className="text-gray-600">{formData.maxTokens.toLocaleString()}</p>
                      </div>
                    </div>

                    <div>
                      <h5 className="font-medium text-gray-900 mb-2">Tools ({formData.tools.length})</h5>
                      <div className="flex flex-wrap gap-2">
                        {formData.tools.map((tool) => (
                          <span key={tool.id} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                            {tool.name}
                          </span>
                        ))}
                        {formData.tools.length === 0 && (
                          <p className="text-gray-500 text-sm">No tools selected</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <h5 className="font-medium text-gray-900 mb-2">Integrations</h5>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-3">
                          <div className={`w-3 h-3 rounded-full ${formData.whatsappConfig.enabled ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                          <span className={`text-sm ${formData.whatsappConfig.enabled ? 'text-green-700 font-medium' : 'text-gray-500'}`}>
                            WhatsApp {formData.whatsappConfig.enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <div className={`w-3 h-3 rounded-full ${formData.telegramConfig.enabled ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                          <span className={`text-sm ${formData.telegramConfig.enabled ? 'text-green-700 font-medium' : 'text-gray-500'}`}>
                            Telegram {formData.telegramConfig.enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>

        {/* Navigation Footer */}
        <div className="bg-white border-t border-gray-200 px-4 sm:px-8 py-4 flex-shrink-0">
          <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-center sm:justify-between gap-3">
            <button
              onClick={previousStep}
              disabled={(isEditMode && currentStep === 1) || (!isEditMode && currentStep === 0)}
              className={`flex items-center space-x-2 px-4 py-2 sm:px-6 sm:py-3 rounded-lg font-medium transition-colors ${
                (isEditMode && currentStep === 1) || (!isEditMode && currentStep === 0)
                  ? 'text-gray-400 cursor-not-allowed bg-gray-100' 
                  : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
              </svg>
              <span className="hidden sm:inline">Previous</span>
            </button>

            {currentStep === totalSteps ? (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex items-center space-x-2 px-6 py-2 sm:px-8 sm:py-3 bg-netlify-teal text-white rounded-lg hover:bg-netlify-600 transition-colors font-medium text-base sm:text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>{isEditMode ? 'Updating...' : 'Creating...'}</span>
                  </>
                ) : (
                  <>
                    <span>{isEditMode ? 'Update Agent' : 'Create Agent'}</span>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                    </svg>
                  </>
                )}
              </button>
            ) : (
              <div className="flex items-center space-x-2 sm:space-x-3">
                {isEditMode && (
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex items-center space-x-2 px-4 py-2 sm:px-6 sm:py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span className="hidden sm:inline">Saving...</span>
                      </>
                    ) : (
                      <>
                        <SaveIcon />
                        <span className="hidden sm:inline">Save</span>
                      </>
                    )}
                  </button>
                )}
                <button
                  onClick={nextStep}
                  className="flex items-center space-x-2 px-6 py-2 sm:px-8 sm:py-3 bg-netlify-teal text-white rounded-lg hover:bg-netlify-600 transition-colors font-medium text-base sm:text-lg"
                >
                  <span>Continue</span>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>


      {/* Prompt Master Sidebar */}
      <div className={`fixed top-0 right-0 h-full w-full sm:w-96 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out z-40 ${
        showPromptHelper ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="p-6 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-gray-900">Prompt Master</h3>
              <button
                onClick={() => setShowPromptHelper(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <p className="text-sm text-gray-600 mt-1">Let me help you write the perfect prompt for your agent</p>
          </div>
          
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {promptHelperMessages.length === 0 && (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-netlify-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-netlify-teal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <h4 className="text-lg font-medium text-gray-900 mb-2">Hi! I'm your Prompt Master</h4>
                <p className="text-gray-600 text-sm mb-4">I'll help you create the perfect instructions for your agent.</p>
                <p className="text-gray-500 text-sm">Tell me what your agent should do, and I'll craft a professional prompt!</p>
              </div>
            )}
            
            {promptHelperMessages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`flex items-start space-x-3 max-w-sm ${message.type === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    message.type === 'user' ? 'bg-netlify-teal' : 'bg-gray-200'
                  }`}>
                    {message.type === 'user' ? (
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    )}
                  </div>
                  <div className={`px-4 py-2 rounded-lg text-sm ${
                    message.type === 'user'
                      ? 'bg-netlify-teal text-white'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {message.content}
                  </div>
                </div>
              </div>
            ))}
            
            {isPromptHelperLoading && (
              <div className="flex justify-start">
                <div className="flex items-start space-x-3 max-w-sm">
                  <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                  </div>
                  <div className="bg-gray-100 px-4 py-2 rounded-lg text-sm">
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200"></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Input */}
          <div className="p-6 border-t border-gray-200 bg-gray-50">
            <div className="flex space-x-3">
              <input
                type="text"
                value={promptHelperInput}
                onChange={(e) => setPromptHelperInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handlePromptHelperSubmit(promptHelperInput)}
                placeholder="Describe what your agent should do..."
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-netlify-teal focus:border-transparent"
                disabled={isPromptHelperLoading}
              />
              <button
                onClick={() => handlePromptHelperSubmit(promptHelperInput)}
                disabled={!promptHelperInput.trim() || isPromptHelperLoading}
                className="px-4 py-3 bg-netlify-teal text-white rounded-lg hover:bg-netlify-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Overlay when sidebar is open */}
      {showPromptHelper && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-30 z-30"
          onClick={() => setShowPromptHelper(false)}
        />
      )}
      
      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-6 right-6 z-50 p-4 rounded-lg shadow-lg max-w-md transform transition-all duration-300 ${
          toast.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800' 
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          <div className="flex items-start">
            <div className="flex-shrink-0">
              {toast.type === 'success' ? (
                <CheckCircleIcon className="text-green-400" />
              ) : (
                <ErrorIcon className="text-red-400" />
              )}
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium">{toast.message}</p>
            </div>
            <div className="ml-4 flex-shrink-0">
              <button
                className={`inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  toast.type === 'success' 
                    ? 'text-green-500 hover:bg-green-100 focus:ring-green-600' 
                    : 'text-red-500 hover:bg-red-100 focus:ring-red-600'
                }`}
                onClick={() => setToast(null)}
              >
                <CloseIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AgentCreationWizard;